import { useMemo, useState } from 'react';
import { STRIPE_CUSTOMER_PORTAL_LINK } from 'src/config';
// @mui
import { Grid, Typography, Stack, Skeleton, Box, styled } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useSubscriptions from 'src/hooks/useSubscriptions';
// components
import { GrayBoxSection } from 'src/sections/GrayBoxSection';
import { AccountBadge } from 'src/components/subscription/AccountBadge';
import HubspotContactForm from 'src/components/forms/HubspotContactForm';
import { contactFormConfig } from 'src/components/forms/hubspot-config';
// @types
import { PlanName, PlanType } from 'src/@types/feature';

const StyledContainer = styled(Stack)(({ theme }) => ({
	marginTop: '0 !important',
  alignItems: 'center',
  justifyContent: 'space-between'
}));


export default function SubscriptionPlanManagement() {
  const [isOpenContactModal, setIsOpenContactModal] = useState(false);

  const {
    subscriptions,
    trialExpiresAt,
    isPremiumUser,
    isCurrentPlanEnterprise,
    subscriptionsAreLoading,
    priceSavingForYear,
    onUpdateToAnnualButtonClick,
    startTrial,
    showStartTrialMessage,
  } = useSubscriptions();
  const { planCurrency } = useSubscriptions();

  const currentPlan = subscriptions?.currentSubscriptions?.find((el) => el?.planLevel > 10 && !el?.trial);

  const currentPlanName = currentPlan && PlanName[currentPlan?.planLevel];
  const currentPlanNameToDisplay = currentPlanName && currentPlanName?.charAt(0).toUpperCase() + currentPlanName?.slice(1)?.toLowerCase();

  const formattedTrialExpirationDate = useMemo(() => 
      trialExpiresAt && new Date(trialExpiresAt).toLocaleDateString("en-NZ", { month: 'long', day: 'numeric' }), 
      [trialExpiresAt]);

  const priceSaving = currentPlan?.planLevel === PlanName.STARTER
    ? priceSavingForYear[PlanName.STARTER]
    : priceSavingForYear[PlanName.ADVANCED];

  const currentPlanStarterMonthly = subscriptions?.currentSubscriptions?.find(el => el?.recurringInterval === PlanType.MONTHLY && el?.planLevel === PlanName.STARTER && !el?.trial);

  // TODO: Forward compatibility to prod in case we won't have plans ready
  // Can be removed saas is set up in prod
  if (!STRIPE_CUSTOMER_PORTAL_LINK) return null;

  return (
    <Grid item xs={12} sx={{ '& .MuiButton-root': { minWidth: 150 } }}>
      <GrayBoxSection title={'Subscription plan'} >
        <Grid item xs={12} sx={{ p: 2 }}>
          <Stack direction="column" spacing={1}>
            { subscriptionsAreLoading ? <>
              <Typography variant={'h6'}><Skeleton width={200}/></Typography>
              <Skeleton width={500}/>
              <Skeleton width={300}/>
              <Skeleton width={300}/>
            </> : <Stack spacing={0.5}>
              {currentPlan?.planLevel !== 10 && currentPlanNameToDisplay &&
                <Stack direction="row" gap={2} alignItems="center" flexWrap={'wrap'} justifyContent={'space-between'}>
                  <Stack spacing={1} direction={'row'} alignItems="center">
                    <Typography sx={{ mt: '2px' }} variant='h6'>{currentPlanNameToDisplay} plan</Typography>
                    <AccountBadge/>
                  </Stack>

                  {isCurrentPlanEnterprise &&
                    <LoadingButton
                      variant="contained"
                      onClick={() => setIsOpenContactModal(true)}
                    >
                      Contact us
                    </LoadingButton>
                  }
                </Stack>
              }
             
              {showStartTrialMessage &&
                <StyledContainer spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, my: .5 }}>Start a free trial to access Carbonclick tools and features</Typography>
                    <Typography color="primary">Your free trial will let you explore tools and features to start your sustainability journey</Typography>
                  </Box>
                  <LoadingButton
                    variant="contained"
                    loading={subscriptionsAreLoading}
                    onClick={startTrial}
                  >
                    Start free trial
                  </LoadingButton>
                </StyledContainer>
              }

              {trialExpiresAt && !isPremiumUser && <>
                <Typography sx={{ fontWeight: 600 }}>Free trial</Typography>
                <Typography>Your trial ends on{' '}
                  <Typography component='span' sx={{ fontWeight: 600 }}>{formattedTrialExpirationDate}</Typography>
                </Typography>
                <Typography color="primary">Maintain access to CarbonClick with tools, features and more by upgrading to a paid subscription</Typography>
              </>}

              {!showStartTrialMessage && !trialExpiresAt && !isPremiumUser && <>
                <Typography sx={{ fontWeight: 600 }}>Free trial</Typography>
                <Typography>Your trial has expired</Typography>
                <Typography color="primary">Maintain access to CarbonClick with tools, features and more by upgrading to a paid subscription</Typography>
              </>}

              {isPremiumUser && currentPlan?.expiresAt && <>
                <Typography>Your plan renews on{' '}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {currentPlan?.expiresAt && new Date(currentPlan?.expiresAt)?.toLocaleDateString("en-NZ", { month: 'long', day: 'numeric' })}
                  </Typography>
                </Typography>
              </>}

              {isPremiumUser &&
                currentPlan?.recurringInterval === PlanType.MONTHLY &&
                currentPlan?.planLevel !== PlanName.ENTERPRISE && (
                  <StyledContainer spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                    <Typography color="primary">
                      {priceSaving 
                        ? `${priceSaving} by upgrading your subscription to an annual plan`
                        : 'Get access to more tools and features by upgrading your plan'}
                    </Typography>
                    <LoadingButton
                      onClick={() => planCurrency && onUpdateToAnnualButtonClick(currentPlanStarterMonthly ? 20 : 30, planCurrency, PlanType.ANNUAL)}
                      variant="contained"
                      className='branded'
                    >
                      Upgrade to annual
                    </LoadingButton>
                  </StyledContainer>
                )}

                {isPremiumUser && currentPlan?.recurringInterval === PlanType.ANNUAL && (
                  <StyledContainer spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                    {currentPlan?.planLevel === PlanName.STARTER
                      ? <Typography color="primary" gutterBottom>Get access to more tools and features by upgrading your plan</Typography>
                      : <Typography color="primary" gutterBottom>Need more custom tools or features? Talk to us to see if an Enterprise plan is right for you</Typography>
                    }
                    <StyledContainer spacing={{ xs: 1, sm: 2 }} direction={{ xs: 'column', sm: 'row' }}>
                      {currentPlan?.planLevel === PlanName.STARTER &&
                      <LoadingButton
                        variant="contained"
                        onClick={() => planCurrency && onUpdateToAnnualButtonClick(30, planCurrency, PlanType.MONTHLY)}
                        className='branded'
                      >
                        Upgrade to Advanced
                      </LoadingButton>
                    }
                    {currentPlan?.planLevel === PlanName.ADVANCED &&
                      <LoadingButton
                        variant="contained"
                        onClick={() => setIsOpenContactModal(true)}
                      >
                        Contact us
                      </LoadingButton>
                    }
                    {currentPlan?.recurringInterval === 'year' &&
                        <LoadingButton
                            variant="contained"
                            href={STRIPE_CUSTOMER_PORTAL_LINK}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                          Manage Subscription
                        </LoadingButton>
                    }
                    </StyledContainer>
                  </StyledContainer>
                )}
              </Stack>
            }
          </Stack>
        </Grid>
      </GrayBoxSection>

      {isOpenContactModal && (
        <HubspotContactForm
          isModalOpen={isOpenContactModal}
          setIsModalOpen={setIsOpenContactModal}
          region={contactFormConfig.region}
          portalId={contactFormConfig.portalId}
          formId={contactFormConfig.formId}
        />
      )}
    </Grid>
  )

}
